
import React, { useState, useCallback, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { usePassword } from '@/contexts/password'; 
import { useResponsive } from '@/hooks/use-responsive';
import Sidebar from './Sidebar';
import LockScreen from './LockScreen';
import MainContent from './MainContent';

interface AppLayoutProps {
  children: React.ReactNode;
}

const AppLayout = ({ children }: AppLayoutProps) => {
  const location = useLocation();
  const { isMobile } = useResponsive();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const { hasPassword, clearAuthSession } = usePassword();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isAppLoaded, setIsAppLoaded] = useState(false);

  // Mark the app as loaded after a short delay
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsAppLoaded(true);
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  // Force authentication check on every app load/refresh
  useEffect(() => {
    // Clear any existing auth session when the component mounts (on app load/refresh)
    if (hasPassword) {
      console.log('App refreshed/restarted - enforcing authentication check');
      clearAuthSession();
      setIsAuthenticated(false);
    } else {
      // If no password is set, automatically authenticate
      setIsAuthenticated(true);
    }

    // Listen for storage changes (if password is changed in another tab)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'nalc_auth_session' || e.key === 'nalc_auth_password') {
        checkAuth();
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [hasPassword, clearAuthSession]);

  // Check authentication status when needed
  const checkAuth = useCallback(() => {
    // If no password is set, automatically authenticate
    if (!hasPassword) {
      setIsAuthenticated(true);
      return;
    }

    // Check if there's a valid auth session
    const session = localStorage.getItem('nalc_auth_session');
    if (session) {
      try {
        const { expires } = JSON.parse(session);
        if (new Date(expires) > new Date()) {
          setIsAuthenticated(true);
          return;
        }
      } catch (e) {
        // Invalid session format
        console.error("Error parsing auth session:", e);
      }
    }
    
    // Not authenticated
    setIsAuthenticated(false);
  }, [hasPassword]);

  // Add back button support
  useEffect(() => {
    const handleBackButton = () => {
      if (location.pathname !== '/') {
        window.history.back();
      }
    };

    window.history.pushState(null, '', window.location.pathname);
    window.addEventListener('popstate', handleBackButton);

    return () => {
      window.removeEventListener('popstate', handleBackButton);
    };
  }, [location.pathname]);

  const toggleSidebar = useCallback(() => {
    setIsSidebarOpen(prev => !prev);
  }, []);

  const closeSidebar = useCallback(() => {
    setIsSidebarOpen(false);
  }, []);

  // Handler for successful authentication
  const handleAuthenticated = useCallback(() => {
    console.log("Authentication successful, updating UI...");
    setIsAuthenticated(true);
  }, []);

  // Show password protection if app is loaded, has password, and not authenticated
  const showLockScreen = isAppLoaded && hasPassword && !isAuthenticated;
  const showContent = !hasPassword || isAuthenticated;

  return (
    <>
      <LockScreen 
        showLockScreen={showLockScreen} 
        onAuthenticated={handleAuthenticated} 
      />
      
      <div className="flex h-screen overflow-hidden bg-background text-foreground">
        {/* Mobile overlay */}
        {isMobile && isSidebarOpen && (
          <div 
            className="fixed inset-0 bg-black/50 z-40"
            onClick={closeSidebar}
            aria-hidden="true"
          />
        )}
        
        {/* Sidebar */}
        <Sidebar 
          isMobile={isMobile} 
          isSidebarOpen={isSidebarOpen}
          onCloseSidebar={closeSidebar}
        />

        {/* Main Content */}
        <MainContent
          isMobile={isMobile}
          toggleSidebar={toggleSidebar}
          showContent={showContent}
        >
          {children}
        </MainContent>
      </div>
    </>
  );
};

export default AppLayout;
